export type ConditionalOperator =
  | "truthy"
  | "falsy"
  | "eq"
  | "ne"
  | "gt"
  | "lt"
  | "gte"
  | "lte";

export const OPERATOR_LABELS: Record<ConditionalOperator, string> = {
  truthy: "has a value",
  falsy: "has no value",
  eq: "equals",
  ne: "does not equal",
  gt: "is greater than",
  lt: "is less than",
  gte: "is greater than or equal to",
  lte: "is less than or equal to"
};

export const OPERATORS_REQUIRING_VALUE: ConditionalOperator[] = [
  "eq",
  "ne",
  "gt",
  "lt",
  "gte",
  "lte"
];

export const OPERATOR_OPTIONS = [
  { key: "truthy", title: "has a value" },
  { key: "falsy", title: "has no value" },
  { key: "eq", title: "equals" },
  { key: "ne", title: "does not equal" },
  { key: "gt", title: "is greater than" },
  { key: "lt", title: "is less than" },
  { key: "gte", title: "is greater than or equal to" },
  { key: "lte", title: "is less than or equal to" }
];

const buildHandlebarsOpen = (
  condition: string,
  operator: ConditionalOperator,
  value: string | null
): string => {
  switch (operator) {
    case "truthy":
      return `{{#if ${condition}}}`;
    case "falsy":
      return `{{#unless ${condition}}}`;
    case "eq":
      return `{{#if (eq ${condition} "${value}")}}`;
    case "ne":
      return `{{#if (ne ${condition} "${value}")}}`;
    case "gt":
      return `{{#if (gt ${condition} "${value}")}}`;
    case "lt":
      return `{{#if (lt ${condition} "${value}")}}`;
    case "gte":
      return `{{#if (gte ${condition} "${value}")}}`;
    case "lte":
      return `{{#if (lte ${condition} "${value}")}}`;
    default:
      return `{{#if ${condition}}}`;
  }
};

const buildHandlebarsClose = (operator: ConditionalOperator): string => {
  return operator === "falsy" ? "{{/unless}}" : "{{/if}}";
};

/**
 * Serialize editor HTML (with <div data-conditional-block> nodes) to Handlebars template string.
 * Called at onChange boundary before passing to parent.
 */
export const serializeToTemplate = (html: string): string => {
  if (!html || !html.includes("data-conditional-block")) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const processNode = (node: Element): string => {
    if (
      node.tagName === "DIV" &&
      node.hasAttribute("data-conditional-block")
    ) {
      const condition = node.getAttribute("condition") ?? "";
      const operator = (node.getAttribute("operator") ?? "truthy") as ConditionalOperator;
      const value = node.getAttribute("value");

      const open = buildHandlebarsOpen(condition, operator, value);
      const close = buildHandlebarsClose(operator);

      // Get inner HTML by serializing child nodes
      let innerHtml = "";
      node.childNodes.forEach(child => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          innerHtml += processNode(child as Element);
        } else if (child.nodeType === Node.TEXT_NODE) {
          innerHtml += child.textContent ?? "";
        }
      });

      return `\n${open}\n${innerHtml}\n${close}\n`;
    }

    // For non-conditional elements, serialize normally but recurse into children
    const tag = node.tagName.toLowerCase();
    const attrs = Array.from(node.attributes)
      .map(a => `${a.name}="${a.value}"`)
      .join(" ");
    const attrStr = attrs ? ` ${attrs}` : "";

    let innerHtml = "";
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        innerHtml += processNode(child as Element);
      } else if (child.nodeType === Node.TEXT_NODE) {
        innerHtml += child.textContent ?? "";
      }
    });

    return `<${tag}${attrStr}>${innerHtml}</${tag}>`;
  };

  let result = "";
  doc.body.childNodes.forEach(child => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      result += processNode(child as Element);
    } else if (child.nodeType === Node.TEXT_NODE) {
      result += child.textContent ?? "";
    }
  });

  return result;
};

/**
 * Parse a Handlebars template string back into editor HTML
 * (converting {{#if ...}}...{{/if}} blocks to <div data-conditional-block> nodes).
 * Called before passing `content` to useEditor.
 */
export const parseFromTemplate = (template: string): string => {
  if (!template) return template;

  let result = template;

  // Helper form with comparison: {{#if (helper field "value")}}...{{/if}}
  result = result.replace(
    /\{\{#if \((\w+) ([\w.[\]]+) "([^"]*)"\)\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_match, helper, condition, value, inner) => {
      const operator = helper as ConditionalOperator;
      return `<div data-conditional-block condition="${condition}" operator="${operator}" value="${value}">${inner.trim()}</div>`;
    }
  );

  // Simple truthy: {{#if field}}...{{/if}}
  result = result.replace(
    /\{\{#if ([\w.[\]]+)\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_match, condition, inner) => {
      return `<div data-conditional-block condition="${condition}" operator="truthy">${inner.trim()}</div>`;
    }
  );

  // Falsy (unless): {{#unless field}}...{{/unless}}
  result = result.replace(
    /\{\{#unless ([\w.[\]]+)\}\}([\s\S]*?)\{\{\/unless\}\}/g,
    (_match, condition, inner) => {
      return `<div data-conditional-block condition="${condition}" operator="falsy">${inner.trim()}</div>`;
    }
  );

  return result;
};

/**
 * Build a human-readable label for display in the conditional block header.
 */
export const buildConditionLabel = (
  fieldTitle: string,
  operator: ConditionalOperator,
  value: string | null
): string => {
  const opLabel = OPERATOR_LABELS[operator] ?? operator;
  if (operator === "truthy" || operator === "falsy") {
    return `${fieldTitle} ${opLabel}`;
  }
  return `${fieldTitle} ${opLabel} "${value}"`;
};
